import React, { useState, useEffect, useRef } from "react";
import "./pf.css";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaRegEye,
  FaRegStickyNote,
  FaEdit,
  FaCheck,
  FaTimes,
  FaExclamationCircle,
  FaInfoCircle,
} from "react-icons/fa";
import axios from "axios";
import PfManagementLayout from "../../Components/pfManagementLayout";

const PFManagement = () => {
  const months = ["Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar", "Apr", "May"];
  const [pfData, setPfData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [newContribution, setNewContribution] = useState("");
  const [hoveredCell, setHoveredCell] = useState(null);
  const [clickedCell, setClickedCell] = useState(null);
  const [financialYear, setFinancialYear] = useState(new Date().getMonth() >= 5 ? new Date().getFullYear() : new Date().getFullYear() - 1);
  const [currentPage, setCurrentPage] = useState(1);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedContribution, setSelectedContribution] = useState(null);
  const [newNotes, setNewNotes] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [isFullWithdrawal, setIsFullWithdrawal] = useState(false);
  const [includeRemainingWithdrawable, setIncludeRemainingWithdrawable] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const entriesPerPage = 15;
  const tooltipRef = useRef(null);

  // Fetch PF data
  useEffect(() => {
    const fetchPFData = async () => {
      try {
        setLoading(true);
        
        // Fetch PF data
        const pfResponse = await axios.get(`https://backend.ems.hiradentallab.com/api/pf/getAllPF?year=${financialYear}`);
        console.log("Fetched PF Data:", pfResponse.data);
        
        // Process the data to ensure monthlyData is properly structured
        const processedData = pfResponse.data.map(user => {
          // Ensure monthlyData exists and is properly formatted
          const monthlyData = user.monthlyData || {};
          
          // Ensure all months have at least an empty object
          months.forEach(month => {
            if (!monthlyData[month]) {
              monthlyData[month] = {
                totalContribution: 0,
                monthBalance: 0,
                monthRetained: 0,
                remainingWithdrawable: 0,
                details: []
              };
            }
          });
          
          return {
            ...user,
            monthlyData: monthlyData
          };
        });
        
        setPfData(processedData);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch PF data");
        setLoading(false);
      }
    };
    fetchPFData();
    const interval = setInterval(fetchPFData, 30000);
    return () => clearInterval(interval);
  }, [financialYear]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        const isContributionCell = event.target.closest(".has-contributions");
        if (!isContributionCell) {
          setClickedCell(null);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
        setError(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  const handleAddOrEditContribution = (user) => {
    setSelectedUser(user);
    setNewContribution(user.previousBalance?.toString() || "");
    setShowModal(true);
    setError(null);
  };

  const handleSaveContribution = async () => {
    if (!newContribution || isNaN(newContribution) || Number(newContribution) < 0) {
      setError("Contribution amount must be a number greater than or equal to 0");
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const contribution = Number(newContribution);
      const endpoint = selectedUser.hasPreviousBalance
        ? "https://backend.ems.hiradentallab.com/api/pf/editPreviousBalance"
        : "https://backend.ems.hiradentallab.com/api/pf/manual";
      const payload = selectedUser.hasPreviousBalance
        ? { userId: selectedUser.userId, balance: contribution }
        : {
            userId: selectedUser.userId,
            totalSalary: (await axios.get(`https://backend.ems.hiradentallab.com/api/user/user/${selectedUser.userId}`)).data.salary || 0,
            balance: contribution,
            isManual: true,
            isEdit: false,
          };
      const response = await axios.post(endpoint, payload);
      console.log("Save Contribution Response:", response.data);
      const updatedData = await axios.get(`https://backend.ems.hiradentallab.com/api/pf/getAllPF?year=${financialYear}`);
      setPfData(updatedData.data);
      setSuccessMessage(
        `PF ${selectedUser.hasPreviousBalance ? "edited" : "added"} successfully for ${selectedUser.name}`
      );
      setShowModal(false);
      setNewContribution("");
      setSelectedUser(null);
      setIsSubmitting(false);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save contribution");
      setIsSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setShowModal(false);
    setNewContribution("");
    setSelectedUser(null);
    setError(null);
  };

  const handleNotesClick = (contribution, userId, month) => {
    const user = pfData.find(u => u.userId === userId);
    setSelectedContribution({ ...contribution, userId, month, user });
    setNewNotes(contribution.notes || "");
    setWithdrawAmount("");
    setIsFullWithdrawal(false);
    setIncludeRemainingWithdrawable(false);
    setShowNotesModal(true);
    setEditMode(true);
    setError(null);
  };

  // Calculate years of service
  const calculateYearsOfService = (joinDate) => {
    if (!joinDate) return 0;
    try {
      const join = new Date(joinDate);
      if (isNaN(join.getTime())) return 0;
      
      const today = new Date();
      
      let years = today.getFullYear() - join.getFullYear();
      const monthDiff = today.getMonth() - join.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < join.getDate())) {
        years--;
      }
      
      const exactYears = (today - join) / (1000 * 60 * 60 * 24 * 365.25);
      
      return Math.max(0, exactYears);
    } catch (error) {
      console.error("Error calculating years of service:", error);
      return 0;
    }
  };

  // Check if user is eligible for 80% withdrawal
  const isEligibleFor80Percent = (joinDate) => {
    if (!joinDate) return false;
    const yearsOfService = calculateYearsOfService(joinDate);
    return yearsOfService >= 3;
  };

  const handleSaveNotes = async () => {
    if (!selectedContribution) return;
    
    setIsSubmitting(true);
    setError(null);
    try {
      if (withdrawAmount && Number(withdrawAmount) > 0) {
        const user = pfData.find((u) => u.userId === selectedContribution.userId);
        const monthlyData = user?.monthlyData || {};
        let lastRemainingWithdrawable = 0;
        
        // Find last remaining withdrawable balance
        for (let i = months.length - 1; i >= 0; i--) {
          const monthKey = months[i];
          if (monthlyData[monthKey] && monthlyData[monthKey].details.some((d) => d.isWithdrawal)) {
            const lastWithdrawal = monthlyData[monthKey].details
              .filter((d) => d.isWithdrawal)
              .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
            if (lastWithdrawal) {
              lastRemainingWithdrawable = lastWithdrawal.remainingWithdrawable || 0;
            }
            break;
          }
        }
        
        const availableBalance = user?.remainingBalance || 0;
        const totalAvailable = includeRemainingWithdrawable
          ? availableBalance + lastRemainingWithdrawable
          : availableBalance;
        
        // Check eligibility for 80% withdrawal
        const isEligible80Percent = isEligibleFor80Percent(user?.joinDate);
        const maxWithdrawalPercentage = isEligible80Percent ? 0.8 : 0.5;
        
        if (Number(withdrawAmount) <= 0) {
          setError("Withdrawal amount must be greater than 0");
          setIsSubmitting(false);
          return;
        }
        
        if (isFullWithdrawal && Number(withdrawAmount) !== totalAvailable) {
          setError("Full withdrawal amount must match the available withdrawable balance");
          setIsSubmitting(false);
          return;
        }
        
        if (!isFullWithdrawal && Number(withdrawAmount) > totalAvailable) {
          setError(`Withdrawal amount cannot exceed available balance of ${totalAvailable} Rs`);
          setIsSubmitting(false);
          return;
        }
        
        const response = await axios.post("https://backend.ems.hiradentallab.com/api/pf/withdraw", {
          userId: selectedContribution.userId,
          month: selectedContribution.month,
          amount: Number(withdrawAmount),
          notes: newNotes || `PF withdrawal for ${selectedContribution.month}`,
          isFullWithdrawal,
          includeRemainingWithdrawable,
        });
        
        setSuccessMessage(
          `Successfully withdrawn Rs. ${withdrawAmount} ${isFullWithdrawal ? `(Full ${maxWithdrawalPercentage * 100}% withdrawal)` : ""}`
        );
        const updatedData = await axios.get(`https://backend.ems.hiradentallab.com/api/pf/getAllPF?year=${financialYear}`);
        setPfData(updatedData.data);
      } else {
        await axios.post("https://backend.ems.hiradentallab.com/api/pf/updateNotes", {
          userId: selectedContribution.userId,
          month: selectedContribution.month,
          date: selectedContribution.date,
          notes: newNotes,
        });
        setSuccessMessage(
          `Notes successfully ${selectedContribution.notes ? "updated" : "added"}`
        );
        const response = await axios.get(`https://backend.ems.hiradentallab.com/api/pf/getAllPF?year=${financialYear}`);
        setPfData(response.data);
      }
      setShowNotesModal(false);
      setIsSubmitting(false);
      setIsFullWithdrawal(false);
      setIncludeRemainingWithdrawable(false);
      setWithdrawAmount("");
      setEditMode(false);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save notes/withdrawal");
      setIsSubmitting(false);
    }
  };

  const handleCancelNotes = () => {
    setShowNotesModal(false);
    setSelectedContribution(null);
    setNewNotes("");
    setWithdrawAmount("");
    setIsFullWithdrawal(false);
    setIncludeRemainingWithdrawable(false);
    setEditMode(false);
    setError(null);
  };

  const handleCellHover = (event, userId, month, details) => {
    if (details && details.length > 0) {
      const tooltipWidth = 300;
      const viewportWidth = window.innerWidth;
      let leftPosition = event.clientX + 20;
      if (leftPosition + tooltipWidth > viewportWidth) {
        leftPosition = event.clientX - tooltipWidth - 20;
      }
      const tooltipHeight = 200;
      const viewportHeight = window.innerHeight;
      let topPosition = event.clientY - tooltipHeight - 10;
      if (topPosition < 0) topPosition = event.clientY + 10;
      setHoveredCell({
        userId,
        month,
        details,
        clientX: leftPosition,
        clientY: topPosition,
      });
    }
  };

  const handleCellClick = (event, userId, month, details) => {
    event.stopPropagation();
    if (details && details.length > 0) {
      const tooltipWidth = 300;
      const viewportWidth = window.innerWidth;
      let leftPosition = event.clientX + 20;
      if (leftPosition + tooltipWidth > viewportWidth) {
        leftPosition = event.clientX - tooltipWidth - 20;
      }
      const tooltipHeight = 200;
      const viewportHeight = window.innerHeight;
      let topPosition = event.clientY - tooltipHeight - 10;
      if (topPosition < 0) topPosition = event.clientY + 10;
      const newClickedCell = {
        userId,
        month,
        details,
        clientX: leftPosition,
        clientY: topPosition,
      };
      if (
        clickedCell &&
        clickedCell.userId === userId &&
        clickedCell.month === month
      ) {
        setClickedCell(null);
      } else {
        setClickedCell(newClickedCell);
      }
    }
  };

  const filteredData = pfData
    .filter((row) => {
      return (
        row.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (row.department &&
          row.department.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredData.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(filteredData.length / entriesPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid Date";
      return date.toISOString().split("T")[0];
    } catch (error) {
      console.error("Error formatting date:", dateString, error);
      return "N/A";
    }
  };

  const handlePrevYear = () => {
    setFinancialYear((prev) => prev - 1);
    setClickedCell(null);
    setCurrentPage(1);
  };

  const handleNextYear = () => {
    setFinancialYear((prev) => prev + 1);
    setClickedCell(null);
    setCurrentPage(1);
  };

  const toggleEditMode = () => {
    setEditMode(!editMode);
  };

  const fixDocumentUrl = (url) => {
    if (!url) return "#";
    return url.startsWith("/") ? url : `/${url}`;
  };

  const handleContributionChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) || value === "") {
      setNewContribution(value);
    }
  };

  const handleWithdrawAmountChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) || value === "") {
      setWithdrawAmount(value);
      if (isFullWithdrawal) setIsFullWithdrawal(false);
    }
  };

  // Calculate months employed
  const calculateMonthsEmployed = (joinDate) => {
    if (!joinDate) return 0;
    try {
      const join = new Date(joinDate);
      if (isNaN(join.getTime())) return 0;
      const now = new Date();
      const months = (now.getFullYear() - join.getFullYear()) * 12 + (now.getMonth() - join.getMonth());
      return Math.max(0, months);
    } catch (error) {
      console.error("Error calculating months employed:", error);
      return 0;
    }
  };

  // SIMPLIFIED: Check if user is eligible for PF (completed 3 months)
  const isEligibleForPF = (joinDate) => {
    const monthsEmployed = calculateMonthsEmployed(joinDate);
    return monthsEmployed >= 3;
  };

  // SIMPLIFIED: Check if PF should be shown for a specific month - JUST SHOW IF DATA EXISTS
  const shouldShowPFForMonth = (user, monthName, monthlyData) => {
    // If user is not eligible for PF at all (less than 3 months), don't show anything
    if (!isEligibleForPF(user?.joinDate)) {
      return false;
    }
    
    // If monthlyData has contributions (monthBalance > 0 or totalContribution > 0), show it
    if (monthlyData?.monthBalance > 0 || monthlyData?.totalContribution > 0) {
      return true;
    }
    
    // If there are details with data, show it
    if (monthlyData?.details && monthlyData.details.length > 0) {
      return true;
    }
    
    // Otherwise, don't show
    return false;
  };

  // Get month display value - SIMPLIFIED
  const getMonthDisplayValue = (monthlyData, hasWithdrawal) => {
    if (!monthlyData) return 0;
    
    // If there's a withdrawal in this month, show remaining withdrawable amount
    if (hasWithdrawal) {
      return monthlyData.remainingWithdrawable || 0;
    }
    
    // Otherwise show the month balance
    return monthlyData.monthBalance || 0;
  };

  // Get months that should be shown for a user (for debugging)
  const getMonthsWithData = (user) => {
    const monthsWithData = [];
    months.forEach(month => {
      const monthlyData = user.monthlyData?.[month] || {};
      if (monthlyData.monthBalance > 0 || monthlyData.totalContribution > 0 || 
          (monthlyData.details && monthlyData.details.length > 0)) {
        monthsWithData.push(`${month}: ${monthlyData.monthBalance}`);
      }
    });
    return monthsWithData;
  };

  return (
    <PfManagementLayout>
      <div className="pf-management">
        <div className="header-section">
          <div className="header-left">
            <h1 style={{ color: "#0353a4", marginTop: "10px", textAlign: "left", fontSize: "2rem" }}>
              Provident Fund Management
            </h1>
            <span className="date-range">{`1 Jun ${financialYear} - 31 May ${financialYear + 1}`}</span>
            <hr />
          </div>
        </div>
        <AnimatePresence>
          {(successMessage || error) && (
            <motion.div
              className={successMessage ? "success-message" : "error-message"}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {successMessage || error}
            </motion.div>
          )}
        </AnimatePresence>
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            Loading PF Management...
          </div>
        ) : (
          <div className="table-container">
            <div className="table-controls">
              <div className="date-picker">
                <button onClick={handlePrevYear} className="arrow-btn">{"<"}</button>
                <span className="year-range">{`${financialYear} - ${financialYear + 1}`}</span>
                <button onClick={handleNextYear} className="arrow-btn">{">"}</button>
              </div>
              <input
                type="text"
                placeholder="Search by name or department"
                className="search-input"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <table className="pf-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Salary</th>
                  <th>Join Date</th>
                  <th>Add Contribution</th>
                  <th>Total Contributions</th>
                  <th>Withdrawn Amount</th>
                  <th>Current Balance</th>
                  <th>Total Retained Balance</th>
                  <th>Remaining Withdrawable</th>
                  {months.map((month) => (
                    <th key={month}>{month}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentEntries.length > 0 ? (
                  currentEntries.map((row) => {
                    const isEligible = isEligibleForPF(row.joinDate);
                    const canWithdraw80Percent = isEligibleFor80Percent(row.joinDate);
                    
                    // Debug: Log months with data
                    if (row.name === "Jeon Jungkook") {
                      console.log("Jeon Jungkook months with data:", getMonthsWithData(row));
                      console.log("Jeon Jungkook monthlyData:", row.monthlyData);
                    }
                    
                    return (
                      <tr key={row.userId}>
                        <td>{row.name}</td>
                        <td>{row.totalSalary || 0}</td>
                        <td>{formatDate(row.joinDate)}</td>
                        <td>
                          {isEligible && (
                            <button
                              data-testid={`contribution-btn-${row.userId}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleAddOrEditContribution(row);
                              }}
                              className="add-contribution-btn"
                              title={row.hasPreviousBalance ? "Edit previous balance" : "Add manual PF contribution"}
                            >
                              {row.hasPreviousBalance ? "Edit" : "Add"}
                            </button>
                          )}
                        </td>
                        <td style={{ fontWeight: "bold" }}>
                          {row.totalContributions || 0} Rs
                        </td>
                        <td style={{ fontWeight: "bold" }}>
                          {row.withdrawnAmount || 0} Rs
                        </td>
                        <td style={{ fontWeight: "bold" }}>
                          {row.currentBalance || 0} Rs
                        </td>
                        <td style={{ fontWeight: "bold", color: "#2e7d32" }}>
                          {row.retainedBalance || 0} Rs
                        </td>
                        <td style={{ fontWeight: "bold", color: "#0288d1" }}>
                          {row.remainingBalance || 0} Rs
                          {!canWithdraw80Percent && (
                            <div className="withdrawal-info">
                              (50% eligible)
                            </div>
                          )}
                          {canWithdraw80Percent && (
                            <div className="withdrawal-info">
                              (80% eligible)
                            </div>
                          )}
                        </td>
                        {months.map((month) => {
                          const monthlyData = row.monthlyData?.[month] || {};
                          const hasWithdrawal = monthlyData.details && 
                                                monthlyData.details.some((d) => d.isWithdrawal);
                          
                          // Check if we should show PF for this month - SIMPLIFIED
                          const shouldShow = shouldShowPFForMonth(row, month, monthlyData);
                          
                          const displayAmount = shouldShow ? 
                                                getMonthDisplayValue(monthlyData, hasWithdrawal) : 0;
                          
                          const hasContributions = monthlyData.monthBalance > 0 || 
                                                   monthlyData.totalContribution > 0 || 
                                                   hasWithdrawal;
                          
                          return (
                            <td
                              key={month}
                              className={hasContributions ? "has-contributions" : ""}
                              onMouseEnter={(e) =>
                                hasContributions && handleCellHover(
                                  e,
                                  row.userId,
                                  month,
                                  monthlyData.details || []
                                )
                              }
                              onMouseLeave={() => setHoveredCell(null)}
                              onClick={(e) =>
                                hasContributions && handleCellClick(
                                  e,
                                  row.userId,
                                  month,
                                  monthlyData.details || []
                                )
                              }
                            >
                              {shouldShow ? (
                                <span className="contribution-amount">
                                  {displayAmount} Rs
                                  {hasWithdrawal && (
                                    <FaExclamationCircle className="withdrawal-indicator" />
                                  )}
                                </span>
                              ) : (
                                "-"
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={20} className="no-data">
                      No matching records found
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot className="table-footer">
                <tr className="pf-table-footer">
                  <td>Total</td>
                  <td>{currentEntries.reduce((sum, row) => sum + (row.totalSalary || 0), 0)}</td>
                  <td></td>
                  <td></td>
                  <td>
                    {currentEntries.reduce((sum, row) => sum + (row.totalContributions || 0), 0)} Rs
                  </td>
                  <td>
                    {currentEntries.reduce((sum, row) => sum + (row.withdrawnAmount || 0), 0)} Rs
                  </td>
                  <td>
                    {currentEntries.reduce((sum, row) => sum + (row.currentBalance || 0), 0)} Rs
                  </td>
                  <td>
                    {currentEntries.reduce((sum, row) => sum + (row.retainedBalance || 0), 0)} Rs
                  </td>
                  <td>
                    {currentEntries.reduce((sum, row) => sum + (row.remainingBalance || 0), 0)} Rs
                  </td>
                  <td colSpan={12}></td>
                </tr>
              </tfoot>
            </table>
            <div className="pagination-container">
              <div className="pagination-info">
                Showing {indexOfFirstEntry + 1} to{" "}
                {Math.min(indexOfLastEntry, filteredData.length)} of{" "}
                {filteredData.length} entries
              </div>
              <div className="pagination-buttons">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (number) => (
                    <button
                      key={number}
                      onClick={() => paginate(number)}
                      className={`pagination-btn ${
                        currentPage === number ? "active" : ""
                      }`}
                    >
                      {number}
                    </button>
                  )
                )}
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
        <AnimatePresence>
          {(hoveredCell || clickedCell) &&
            (hoveredCell?.details?.length > 0 ||
              clickedCell?.details?.length > 0) && (
              <motion.div
                ref={tooltipRef}
                className="contribution-tooltip"
                style={{
                  top: (clickedCell || hoveredCell).clientY,
                  left: (clickedCell || hoveredCell).clientX,
                  pointerEvents: clickedCell ? "auto" : "none",
                }}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
              >
                <div className="tooltip-header">
                  <h4>PF Transaction Details</h4>
                  <div className="tooltip-subheader">
                    {(clickedCell || hoveredCell).month} -{" "}
                    {(clickedCell || hoveredCell).details.length} transaction
                    {(clickedCell || hoveredCell).details.length !== 1 ? "s" : ""}
                  </div>
                </div>
                <div className="tooltip-content">
                  {(clickedCell || hoveredCell).details
                    .map((contribution, index) => {
                      const user = pfData.find((u) => u.userId === (clickedCell || hoveredCell).userId);
                      const monthlyData = user?.monthlyData[(clickedCell || hoveredCell).month] || {};
                      const canWithdraw80Percent = isEligibleFor80Percent(user?.joinDate);
                      
                      if (contribution.isWithdrawal) {
                        return (
                          <motion.div
                            key={index}
                            className="contribution-detail"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                          >
                            <div className="contribution-info">
                              <div className="contribution-date">
                                {formatDate(contribution.date)}
                              </div>
                              <div className="contribution-amount withdrawal">
                                Withdrawal: {Math.abs(contribution.amount)} Rs
                                {contribution.isFullWithdrawal && (
                                  <span className="full-withdrawal-tag">
                                    ({canWithdraw80Percent ? 'Full 80%' : 'Full 50%'})
                                  </span>
                                )}
                              </div>
                              <div className="retained-amount">
                                Retained Amount: {contribution.monthRetained || 0} Rs
                              </div>
                              <div className="remaining-withdrawable">
                                Remaining Withdrawable: {contribution.remainingWithdrawable || 0} Rs
                              </div>
                              <div className="service-info">
                                {canWithdraw80Percent ? '80% Withdrawal Eligible' : '50% Withdrawal Eligible'}
                              </div>
                            </div>
                            <div className="contribution-actions">
                              {contribution.document && (
                                <a
                                  href={fixDocumentUrl(contribution.document)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="document-link"
                                  onClick={(e) => {
                                    if (
                                      !contribution.document ||
                                      contribution.document.trim() === ""
                                    ) {
                                      e.preventDefault();
                                      alert("Document not available");
                                    }
                                  }}
                                >
                                  <FaRegEye />
                                </a>
                              )}
                            </div>
                          </motion.div>
                        );
                      } else {
                        const hasWithdrawalThisMonth = monthlyData.details?.some((d) => d.isWithdrawal);
                        if (!hasWithdrawalThisMonth) {
                          const withdrawablePercentage = canWithdraw80Percent ? 0.8 : 0.5;
                          const retainedPercentage = canWithdraw80Percent ? 0.2 : 0.5;
                          const withdrawableAmount = monthlyData.monthBalance * withdrawablePercentage;
                          const retainedAmount = monthlyData.monthBalance * retainedPercentage;
                          
                          return (
                            <motion.div
                              key={index}
                              className="contribution-detail"
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.05 }}
                            >
                              <div className="contribution-info">
                                <div className="contribution-date">
                                  {formatDate(contribution.date)}
                                </div>
                                <div className="contribution-amount deposit">
                                  Contribution: {Math.abs(contribution.amount)} Rs
                                </div>
                                <div className="withdrawable-amount">
                                  Withdrawable ({canWithdraw80Percent ? '80%' : '50%'}): {withdrawableAmount.toFixed(2)} Rs
                                </div>
                                <div className="retained-amount">
                                  Retained ({canWithdraw80Percent ? '20%' : '50%'}): {retainedAmount.toFixed(2)} Rs
                                </div>
                                <div className="service-info">
                                  {canWithdraw80Percent ? '80% Withdrawal Eligible' : '50% Withdrawal Eligible'}
                                </div>
                              </div>
                              <div className="contribution-actions">
                                {contribution.document && (
                                  <a
                                    href={fixDocumentUrl(contribution.document)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="document-link"
                                    onClick={(e) => {
                                      if (
                                        !contribution.document ||
                                        contribution.document.trim() === ""
                                      ) {
                                        e.preventDefault();
                                        alert("Document not available");
                                      }
                                    }}
                                  >
                                    <FaRegEye />
                                </a>
                                )}
                                <button
                                  className="notes-btn"
                                  onClick={() =>
                                    handleNotesClick(
                                      contribution,
                                      (clickedCell || hoveredCell).userId,
                                      (clickedCell || hoveredCell).month
                                    )
                                  }
                                  title={contribution.notes ? "View/Edit Notes" : "Add Notes"}
                                >
                                  <FaRegStickyNote />
                                </button>
                              </div>
                            </motion.div>
                          );
                        }
                        return null;
                      }
                    })}
                </div>
                {clickedCell && (
                  <button
                    className="close-tooltip-btn"
                    onClick={() => setClickedCell(null)}
                  >
                    ×
                  </button>
                )}
              </motion.div>
            )}
        </AnimatePresence>
        <AnimatePresence>
          {showModal && selectedUser && (
            <motion.div
              key={selectedUser.userId}
              className="modal-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                className="modal-content"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ type: "spring", damping: 20 }}
              >
                <div className="modal-header">
                  <h2 style={{ color: "#2e7d32" }}>
                    Edit Previous Balance for {selectedUser.name}
                  </h2>
                  <button
                    className="close-btn"
                    onClick={handleCancelEdit}
                    disabled={isSubmitting}
                  >
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  <div className="previous-balance-info">
                    <FaInfoCircle className="info-icon" />
                    <span>Current Previous Balance (Rs): {selectedUser.previousBalance || 0} Rs</span>
                  </div>
                  <div className="form-group">
                    <label htmlFor="contributionAmount">New Previous Balance (Rs):</label>
                    <input
                      type="number"
                      id="contributionAmount"
                      value={newContribution}
                      onChange={handleContributionChange}
                      className="contribution-input"
                      min="0"
                      placeholder="Enter new previous balance in Rs"
                      onKeyDown={(e) => {
                        if (!/^\d$/.test(e.key) && e.key !== "Backspace" && e.key !== "ArrowUp" && e.key !== "ArrowDown") {
                          e.preventDefault();
                        }
                      }}
                    />
                  </div>
                  {error && (
                    <motion.div
                      className="modal-error"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      {error}
                    </motion.div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={handleCancelEdit}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="submit-btn"
                    onClick={handleSaveContribution}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner"></span> Saving...
                      </>
                    ) : (
                      "Update"
                    )}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {showNotesModal && selectedContribution && (
            <motion.div
              className="modal-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                className="notes-modal-content"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ type: "spring", damping: 20 }}
              >
                <div className="notes-modal-header">
                  <h3>PF Transaction on {formatDate(selectedContribution.date)}</h3>
                  <div className="notes-actions">
                    {!editMode ? (
                      <button
                        className="edit-notes-btn"
                        onClick={toggleEditMode}
                        title="Edit Notes"
                      >
                        <FaEdit />
                      </button>
                    ) : (
                      <>
                        <button
                          className="save-notes-btn"
                          onClick={handleSaveNotes}
                          disabled={isSubmitting}
                          title="Save"
                        >
                          <FaCheck />
                        </button>
                        <button
                          className="cancel-edit-btn"
                          onClick={toggleEditMode}
                          title="Cancel"
                        >
                          <FaTimes />
                        </button>
                      </>
                    )}
                    <button
                      className="close-notes-btn"
                      onClick={handleCancelNotes}
                      disabled={isSubmitting}
                    >
                      ×
                    </button>
                  </div>
                </div>
                <div className="notes-modal-body">
                  <div className="transaction-info">
                    <div className="transaction-amount">
                      {selectedContribution.isWithdrawal ? "Withdrawal" : "Contribution"}:{" "}
                      <span className={selectedContribution.isWithdrawal ? "withdrawal" : "deposit"}>
                        {Math.abs(selectedContribution.amount)} Rs
                      </span>
                    </div>
                    {(() => {
                      const user = pfData.find(u => u.userId === selectedContribution.userId);
                      const canWithdraw80Percent = isEligibleFor80Percent(user?.joinDate);
                      
                      return (
                        <div className="service-info">
                          <FaInfoCircle className="info-icon" />
                          <span>
                            {canWithdraw80Percent 
                              ? 'Eligible for 80% withdrawal (20% retained)' 
                              : 'Eligible for 50% withdrawal (50% retained)'}
                          </span>
                        </div>
                      );
                    })()}
                    {selectedContribution.isWithdrawal && (
                      <>
                        <div className="retained-info">
                          <FaInfoCircle className="info-icon" />
                          <span>Retained Amount: {selectedContribution.monthRetained || 0} Rs</span>
                        </div>
                        <div className="retained-info">
                          <FaInfoCircle className="info-icon" />
                          <span>Remaining Withdrawable: {selectedContribution.remainingWithdrawable || 0} Rs</span>
                        </div>
                      </>
                    )}
                  </div>
                  {editMode && !selectedContribution.isWithdrawal && (
                    <div className="withdraw-section">
                      {(() => {
                        const user = pfData.find((u) => u.userId === selectedContribution.userId);
                        const monthlyData = user?.monthlyData || {};
                        let lastRemainingWithdrawable = 0;
                        for (let i = months.length - 1; i >= 0; i--) {
                          const monthKey = months[i];
                          if (monthlyData[monthKey] && monthlyData[monthKey].details.some((d) => d.isWithdrawal)) {
                            const lastWithdrawal = monthlyData[monthKey].details
                              .filter((d) => d.isWithdrawal)
                              .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
                            if (lastWithdrawal) {
                              lastRemainingWithdrawable = lastWithdrawal.remainingWithdrawable || 0;
                            }
                            break;
                          }
                        }
                        
                        const canWithdraw80Percent = isEligibleFor80Percent(user?.joinDate);
                        const withdrawalPercentage = canWithdraw80Percent ? 0.8 : 0.5;
                        const retainedPercentage = canWithdraw80Percent ? 0.2 : 0.5;
                        
                        const availableBalance = user?.remainingBalance || 0;
                        const totalAvailable = includeRemainingWithdrawable
                          ? availableBalance + lastRemainingWithdrawable
                          : availableBalance;
                        
                        return (
                          <>
                            <div className="available-balance">
                              <FaInfoCircle className="info-icon" />
                              <span>
                                Available for withdrawal ({canWithdraw80Percent ? '80%' : '50%'}): {totalAvailable} Rs
                                {!canWithdraw80Percent && (
                                  <span className="warning-text"> (50% withdrawal limit applies)</span>
                                )}
                              </span>
                            </div>
                            <div className="withdraw-options">
                              <label htmlFor="withdrawAmount">Withdraw Amount (Rs):</label>
                              <input
                                type="number"
                                id="withdrawAmount"
                                value={withdrawAmount}
                                onChange={handleWithdrawAmountChange}
                                className="withdraw-input"
                                min="1"
                                max={totalAvailable}
                                placeholder={`Max: ${totalAvailable} Rs`}
                                disabled={isFullWithdrawal}
                              />
                            </div>
                            <div className="full-withdrawal-option">
                              <input
                                type="checkbox"
                                id="fullWithdrawal"
                                checked={isFullWithdrawal}
                                onChange={(e) => {
                                  setIsFullWithdrawal(e.target.checked);
                                  if (e.target.checked) {
                                    setWithdrawAmount(totalAvailable.toString());
                                  } else {
                                    setWithdrawAmount("");
                                  }
                                }}
                              />
                              <label htmlFor="fullWithdrawal">
                                Full Withdrawal ({canWithdraw80Percent ? '80%' : '50%'}: {totalAvailable} Rs)
                              </label>
                            </div>
                            <div className="include-remaining-option">
                              <input
                                type="checkbox"
                                id="includeRemainingWithdrawable"
                                checked={includeRemainingWithdrawable}
                                onChange={(e) => {
                                  const newInclude = e.target.checked;
                                  setIncludeRemainingWithdrawable(newInclude);
                                  if (isFullWithdrawal) {
                                    const newTotal = newInclude ? availableBalance + lastRemainingWithdrawable : availableBalance;
                                    setWithdrawAmount(newTotal.toString());
                                  }
                                }}
                              />
                              <label htmlFor="includeRemainingWithdrawable">
                                Include Previous Withdrawal's Remaining Balance ({lastRemainingWithdrawable} Rs)
                              </label>
                            </div>
                            {isFullWithdrawal && (
                              <div className="full-withdrawal-warning">
                                <FaExclamationCircle />
                                <span>
                                  This will withdraw your entire available balance ({canWithdraw80Percent ? '80%' : '50%'}) 
                                  and update your PF records. {canWithdraw80Percent ? '20%' : '50%'} will be retained.
                                </span>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                  {editMode ? (
                    <textarea
                      value={newNotes}
                      onChange={(e) => setNewNotes(e.target.value)}
                      className="notes-textarea"
                      placeholder="Enter your notes here..."
                      rows={4}
                      autoFocus
                    />
                  ) : newNotes ? (
                    <div className="notes-display">{newNotes}</div>
                  ) : (
                    <div className="no-notes">No notes added yet</div>
                  )}
                  {error && (
                    <motion.div
                      className="notes-error"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      {error}
                    </motion.div>
                  )}
                </div>
                <div className="notes-modal-footer">
                  <button
                    type="button"
                    className="notes-cancel-btn"
                    onClick={handleCancelNotes}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  {editMode && (
                    <button
                      type="button"
                      className="notes-save-btn"
                      onClick={handleSaveNotes}
                      disabled={isSubmitting || (withdrawAmount && Number(withdrawAmount) <= 0)}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner"></span> Saving...
                        </>
                      ) : (
                        "Save"
                      )}
                    </button>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </PfManagementLayout>
  );
};

export default PFManagement;